import {useSelector} from "react-redux"
import {breakpoints} from "@next/themes"
import {useRef, useState, useCallback, MutableRefObject, useEffect, useLayoutEffect} from "react"
import {useMediaQuery} from "../../hooks/useMediaQuery"

import {selectShouldFacetsBeFixed} from "../../ducks/search"
import {getElementInnerDimensions} from "../../utils/getElementInnerDimensions"
import {FACETS_CONTAINER_OFFSET, FACETS_CONTAINER_MINIMUM_PADDING} from "../../config/constants"
import {
    getWindow,
    getDocumentScrollTop,
    addDocumentScrollListener,
    removeDocumentScrollListener,
} from "../../utils/window"

interface UseShowFacets {
    infoContainerTop: number
    facetAbsoluteTop: number
    containerRef: MutableRefObject<HTMLDivElement | null>
    facetsContainerRef: MutableRefObject<HTMLDivElement | null>
    infoContainerRef: MutableRefObject<HTMLDivElement | null>
    showFixedFacets: boolean
    showOpenFilterBtn: boolean
    underlayElementHeight: number
    handleOpenFilterBtnClick: VoidFunction
    totalProductsHeight: number
    totalProductsRef: MutableRefObject<HTMLDivElement | null>
    filterContainerHeight: number
}

export function useShowFilters(hasReachedFooter: boolean, initialScrollCompleted: boolean): UseShowFacets {
    const lgScreenBreakPoint = useMediaQuery(`(min-width:${breakpoints.values.lg}px)`)
    const facetsContainerRef = useRef<HTMLDivElement | null>(null)
    const scrollTopRef = useRef(0)
    const infoContainerRef = useRef<HTMLDivElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const windowWidthRef = useRef(getWindow()?.innerWidth ?? 0)
    const [facetAbsoluteTop, setFacetAbsoluteTop] = useState(0)
    const [underlayElementHeight, setUnderlayElementHeight] = useState(0)
    const [infoContainerTop, setInfoContainerTop] = useState(0)
    const [showOpenFilterBtn, setShowOpenFilterBtn] = useState(false)
    const [showFixedFacets, setShowFixedFacets] = useState(false)
    const shouldFacetsBeFixed = useSelector(selectShouldFacetsBeFixed)
    const oldShouldFacetsBeFixed = useRef(shouldFacetsBeFixed)
    const hadReachedFooterRef = useRef(hasReachedFooter)
    const [totalProductsHeight, setTotalProductsHeight] = useState(0)
    const totalProductsRef = useRef<HTMLDivElement | null>(null)
    const [filterContainerHeight, setFilterContainerHeight] = useState(0)
    const isInitialLoadComplete = useRef(false)

    const openFixedFacets = useCallback(() => {
        setShowOpenFilterBtn(false)
        setFacetAbsoluteTop(getDocumentScrollTop() + totalProductsHeight + FACETS_CONTAINER_OFFSET)
        setShowFixedFacets(false)
        setUnderlayElementHeight(0)
        setInfoContainerTop(0)
        hadReachedFooterRef.current = hasReachedFooter
    }, [hasReachedFooter, totalProductsHeight])

    useLayoutEffect(() => {
        if (initialScrollCompleted && !isInitialLoadComplete.current) {
            isInitialLoadComplete.current = true
            openFixedFacets()
        }
    }, [initialScrollCompleted, openFixedFacets])

    useLayoutEffect(() => {
        const height: number =
            totalProductsRef.current?.clientHeight === undefined ? 0 : totalProductsRef.current.clientHeight
        setTotalProductsHeight(height)
    }, [totalProductsRef.current?.clientHeight])

    const handleScroll = useCallback(() => {
        if (hasReachedFooter) {
            setShowOpenFilterBtn(false)
        }

        const scrollTop = getDocumentScrollTop()
        const wentBackToTop = scrollTop === 0 && scrollTopRef.current > 0

        // bail out if we don't have refs for all the elements
        if (!facetsContainerRef.current || !infoContainerRef.current || !containerRef.current) return

        const skipUpdate = shouldFacetsBeFixed || (hasReachedFooter && !wentBackToTop)

        if (skipUpdate) {
            setInfoContainerTop(0)
            return
        }

        const {innerWidth} = getWindow() as any
        const isSameWindowWidth = windowWidthRef.current === innerWidth
        const isScrollingDown = scrollTop > scrollTopRef.current
        const containerHeight = containerRef.current.clientHeight - infoContainerRef.current.offsetHeight
        setFilterContainerHeight(containerHeight)
        const {height} = getElementInnerDimensions(facetsContainerRef.current)
        const relativeScrollHeight = height + facetAbsoluteTop

        if (containerHeight > scrollTop) {
            setInfoContainerTop(0)
        } else if (containerHeight) {
            setInfoContainerTop(scrollTop)
        }

        if (isScrollingDown && facetAbsoluteTop < scrollTop && isSameWindowWidth) {
            setShowFixedFacets(false)
            setUnderlayElementHeight(0)
            setShowOpenFilterBtn(
                scrollTop - relativeScrollHeight + FACETS_CONTAINER_OFFSET + FACETS_CONTAINER_MINIMUM_PADDING >= 0,
            )
        } else if (!isScrollingDown && scrollTop !== scrollTopRef.current) {
            if (facetAbsoluteTop > scrollTop && isSameWindowWidth) {
                setShowFixedFacets(true)
                setFacetAbsoluteTop(scrollTop)
                if (!underlayElementHeight) {
                    const clientHeight = facetsContainerRef.current?.clientHeight ?? 0
                    setUnderlayElementHeight(clientHeight + scrollTop)
                }
            } else if (relativeScrollHeight < scrollTop && isSameWindowWidth) {
                setFacetAbsoluteTop(scrollTop + FACETS_CONTAINER_OFFSET + FACETS_CONTAINER_MINIMUM_PADDING - height)
            }
            setShowOpenFilterBtn(false)
        }

        scrollTopRef.current = scrollTop
        windowWidthRef.current = innerWidth
    }, [facetAbsoluteTop, shouldFacetsBeFixed, hasReachedFooter, underlayElementHeight])

    useLayoutEffect(() => {
        if (shouldFacetsBeFixed && initialScrollCompleted) {
            oldShouldFacetsBeFixed.current = shouldFacetsBeFixed
            openFixedFacets()
        }

        if (!shouldFacetsBeFixed && oldShouldFacetsBeFixed.current && !hadReachedFooterRef.current) {
            oldShouldFacetsBeFixed.current = false
            setFacetAbsoluteTop(getDocumentScrollTop())
        }
    }, [shouldFacetsBeFixed, openFixedFacets, initialScrollCompleted])

    useLayoutEffect(() => {
        if (shouldFacetsBeFixed || oldShouldFacetsBeFixed.current) {
            return
        }

        const scrollTop = getDocumentScrollTop()
        if (scrollTop > 0) {
            setFacetAbsoluteTop(scrollTop + FACETS_CONTAINER_MINIMUM_PADDING)
            setShowFixedFacets(false)
            setShowOpenFilterBtn(false)
        }
    }, [lgScreenBreakPoint, shouldFacetsBeFixed])

    useEffect(() => {
        if (lgScreenBreakPoint) addDocumentScrollListener(handleScroll)

        return () => {
            removeDocumentScrollListener(handleScroll)
        }
    }, [handleScroll, lgScreenBreakPoint])

    return {
        containerRef,
        infoContainerTop,
        facetAbsoluteTop,
        facetsContainerRef,
        infoContainerRef,
        showFixedFacets,
        showOpenFilterBtn,
        handleOpenFilterBtnClick: openFixedFacets,
        underlayElementHeight,
        totalProductsHeight,
        totalProductsRef,
        filterContainerHeight,
    }
}
